import {isNotNil} from 'ramda';

import {
  ACCOUNTING_SUBJECT_VOCABULARY,
  BackgroundNotificationAccountingSubjectChangedPayload,
} from '@dms/api/core';

import {findAccountingSubjectContextId} from './findAccountingSubjectContextId';

interface AccountingSubjectChangeTarget {
  checkoutId: string;
  orderId: string;
}

/**
 * Decides whether an accounting-subject doorbell concerns the order on screen.
 *
 * Mandatory, not an optimisation: data from the external accounting has no
 * initiating user, so the backend rings every user of the business case's branch.
 * The decision reads only the context, never the subject — the subject names
 * whatever the backend tracks (today the order, possibly a payment later), while
 * the context always locates the checkout and the order; a context missing either
 * cannot be placed and is dropped. `reason` is deliberately ignored — an unknown
 * one still means "something changed, refetch".
 */
export const isAccountingSubjectChangeForOrder = (
  payload: BackgroundNotificationAccountingSubjectChangedPayload,
  target: AccountingSubjectChangeTarget
) => {
  // Runs inside the subscription effect; a payload off the contract must not throw there.
  const isWellFormed =
    isNotNil(payload) &&
    Array.isArray(payload.context) &&
    payload.context.every(
      (entry) => isNotNil(entry) && typeof entry.type === 'string' && typeof entry.id === 'string'
    );

  if (!isWellFormed) {
    return false;
  }

  const {CHECKOUT_ID, ORDER_ID} = ACCOUNTING_SUBJECT_VOCABULARY.contextTypes;

  return (
    findAccountingSubjectContextId(payload.context, CHECKOUT_ID) === target.checkoutId &&
    findAccountingSubjectContextId(payload.context, ORDER_ID) === target.orderId
  );
};
